import { React, useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getClass,
  getStudents,
  getQR,
  getWatchlistStudents,
  getCountryId,
  getCountryGrades,
  getActiveGradeLevel3Schools,
} from "../services/APIClient";

import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import SENWatchlistOverview from "../components/SENWatchlistOverview";
import OrientationPrompt from "../components/OrientationPrompt";

import { Container, Row, Col, Card, Alert } from "react-bootstrap";
import { formatAcademicYear } from "../utils/academicYear";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";

import "../components/ClassOverview.css";

const getStudentAcademicYears = (student) => [
  student?.academicYear,
  student?.academic_year,
  student?.year,
  student?.class?.academicYear,
  student?.class?.academic_year,
  student?.watchlistEntry?.academicYear,
  student?.watchlistEntry?.academic_year,
  ...(student?.classes || []).flatMap((classItem) => [
    classItem?.academicYear,
    classItem?.academic_year,
  ]),
  ...(student?.enrolments || []).flatMap((enrolment) => [
    enrolment?.academicYear,
    enrolment?.academic_year,
  ]),
].filter(Boolean);

function SENWatchlist({ asOfDate }) {
  const location = useLocation();

  let classDetails = location.state?.classDetails?.classDetails;
  let teachers = location.state?.teachers?.teachers;
  let assessment = location.state?.assessment?.assessment;

  let schoolid = location.state?.schoolid?.schoolid;
  let teacherid = location.state?.teacherid?.teacherid;

  let studentsReadingScore =
    location.state?.studentsReadingScore?.studentsReadingScore;

  let readingScores = location.state?.readingScores?.readingScores;

  const [selectedYear, setSelectedYear] = useState(null);
  // Watchlist users can select the current and immediately previous year.
  const academicYearOptions = useAcademicYearOptions({ asOfDate, previousYears: 1 });
  const selectedAcademicYear = academicYearOptions.find(
    ({ display }) => display === selectedYear
  );
  const previousCurrentYear = useRef(academicYearOptions[0]?.display);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [group, setGroup] = useState("");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [gradeLevel3Schools, setGradeLevel3Schools] = useState([]);
  const [missingYearAssociationCount, setMissingYearAssociationCount] = useState(0);

  const isStaging = false;

  console.log(students, "students");

  const fetchSenStudents = async () => {
    console.log("🔍 fetchSenStudents called with schoolid:", schoolid);
    try {
      setLoading(true);
      console.log("📞 Calling getWatchlistStudents...");
      const data = await getWatchlistStudents(schoolid, false);
      console.log("✅ Received watchlist students:", data);

      setStudents(data);
    } catch (err) {
      console.error("❌ Failed to fetch students:", err);
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Fetch grade level names and grade level 3 schools
  useEffect(() => {
    const fetchGradeData = async () => {
      try {
        if (schoolid) {
          const countryResponse = await getCountryId(schoolid);
          
          if (countryResponse.countryId) {
            const gradesResponse = await getCountryGrades(countryResponse.countryId);
            const { grade1, grade2, grade3 } = gradesResponse;
            
            let gradeOptions = [grade1, grade2];
            
            // Fetch grade level 3 schools
            const activeSchools = await getActiveGradeLevel3Schools();
            setGradeLevel3Schools(activeSchools);
            
            // Add grade3 if enabled for this school
            if ((isStaging || activeSchools.includes(schoolid)) && grade3) {
              gradeOptions.push(grade3);
            }
            
            setGradeLevelOptions(gradeOptions);
          }
        }
      } catch (error) {
        console.error("Error fetching grade data:", error);
      }
    };

    fetchGradeData();
  }, [schoolid, isStaging]);

  useEffect(() => {
    if (schoolid) {
      fetchSenStudents();
    }
  }, [schoolid]);

  useEffect(() => {
    const currentYear = academicYearOptions[0]?.display;
    if (selectedYear === previousCurrentYear.current && currentYear) {
      setSelectedYear(currentYear);
    }
    previousCurrentYear.current = currentYear;
  }, [academicYearOptions, selectedYear]);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  let sharedWith = group.sharedWith;

  // Filter students based on selected year and grade level
  useEffect(() => {
    console.log("🔍 Filtering - selectedYear:", selectedYear);
    console.log("🔍 Filtering - selectedGradeLevel:", selectedGradeLevel);
    console.log("🔍 Students to filter:", students);

    if (!selectedAcademicYear) {
      console.log("❌ No year selected or invalid year range");
      setFilteredStudents([]);
      setMissingYearAssociationCount(0);
      return;
    }

    let missingAssociationCount = 0;
    const filtered = students.filter((student) => {
      const academicYears = getStudentAcademicYears(student);
      const normalizedYears = academicYears.flatMap((year) => {
        try {
          return [formatAcademicYear(year, "storage")];
        } catch (error) {
          return [];
        }
      });
      const hasReliableAssociation = normalizedYears.length > 0;
      const yearMatch =
        !hasReliableAssociation || normalizedYears.includes(selectedAcademicYear.storage);

      if (!selectedGradeLevel) {
        if (!hasReliableAssociation) missingAssociationCount += 1;
        console.log(`✅ ${student.name}: yearMatch=${yearMatch}, no grade filter`);
        return yearMatch;
      }

      const gradeMatch =
        (selectedGradeLevel === "grade_level1" && student.grade_level1) ||
        (selectedGradeLevel === "grade_level2" && student.grade_level2) ||
        (selectedGradeLevel === "grade_level3" && student.grade_level3);
      if (!hasReliableAssociation && gradeMatch) missingAssociationCount += 1;

      console.log(`✅ ${student.name}: yearMatch=${yearMatch}, gradeMatch=${gradeMatch}`);
      return yearMatch && gradeMatch;
    });

    console.log("✅ Filtered students:", filtered);
    setFilteredStudents(filtered);
    setMissingYearAssociationCount(missingAssociationCount);
  }, [students, selectedYear, selectedAcademicYear, selectedGradeLevel]);

  console.log("🎯 SENWatchlist render - students:", students);
  console.log("🎯 filteredStudents:", filteredStudents);

  return (
    <Container fluid className="vh-100">
      <HeadingBanner
        name={""}
        isLoggedIn={true}
        cloud={true}
        teacherid={teacherid}
      />
      <Row className="h-100">
        <LeftPanel />
        <Col sm={{ span: 10, offset: 2 }} className="blue-background">
          {selectedYear && missingYearAssociationCount > 0 && (
            <Alert variant="warning" data-testid="missing-academic-year-warning">
              {missingYearAssociationCount} watchlist student
              {missingYearAssociationCount === 1 ? " is" : "s are"} missing a reliable
              academic-year association. They remain visible until the API data is corrected.
            </Alert>
          )}
          {!selectedYear ? (
            <Card className="dynamic-box">
              <div className="school-info-header mb-3">
                <h4 className="mb-1">SEN Watchlist</h4>
              </div>
              <h5 className="step-indicator">Step 1: Select Academic Year</h5>
              <Row className="justify-content-center mb-3">
                {academicYearOptions.map((year) => (
                  <Col md={4} key={year.storage} className="mb-3">
                    <button
                      className={`year-selection-button w-100 py-3 shadow-sm rounded ${
                        selectedYear === year.display ? "selected" : ""
                      }`}
                      onClick={() => setSelectedYear(year.display)}
                    >
                      <h3>{year.display}</h3>
                      <small className="text-muted d-block mt-2">
                        {year.start.toLocaleDateString(
                          "en-IE",
                          { month: "short", year: "numeric", timeZone: "Europe/Dublin" }
                        )}{" "}
                        -
                        {year.end.toLocaleDateString(
                          "en-IE",
                          { month: "short", year: "numeric", timeZone: "Europe/Dublin" }
                        )}
                      </small>
                    </button>
                  </Col>
                ))}
              </Row>
            </Card>
          ) : !selectedGradeLevel ? (
            <Card className="dynamic-box">
              <div className="school-info-header mb-3">
                <h4 className="mb-1">SEN Watchlist</h4>
              </div>
              <h5 className="step-indicator">Step 2: Select Grade Level</h5>
              <Row className="justify-content-center mb-3">
                {gradeLevelOptions.map((gradeName, index) => (
                  <Col md={4} key={index} className="mb-3">
                    <button
                      className={`year-selection-button w-100 py-3 shadow-sm rounded ${
                        selectedGradeLevel === `grade_level${index + 1}` ? "selected" : ""
                      }`}
                      onClick={() => setSelectedGradeLevel(`grade_level${index + 1}`)}
                    >
                      <h3>{gradeName}</h3>
                    </button>
                  </Col>
                ))}
              </Row>
            </Card>
          ) : (
            <SENWatchlistOverview
              assessment={assessment}
              classDetails={classDetails}
              teachers={teachers}
              sharedWith={sharedWith}
              students={filteredStudents}
              teacherid={teacherid}
              studentsReadingScore={studentsReadingScore}
              schoolid={schoolid}
              readingScores={readingScores}
              selectedYear={selectedYear}
            />
          )}
        </Col>
      </Row>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default SENWatchlist;
